import React from "react";
import { useGame } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button.jsx";
import { getTaskConfig } from "../utils/taskConfig.js";
import {
  getPreLobbyMode,
  getReflectionTiming,
  isBaseRateStaticSyntheticGroupCondition,
  usesPostInitialConditionInstructions,
  usesPostPeerAskAll,
} from "../utils/conditionUtils.js";

// The condition-specific instruction page for the arms that set
// conditionInstructionsTiming: "post-initial". It is the first screen that
// differs across those arms, and it is shown only after the initial estimate
// and the confidence rating are recorded, which is the order
// writeup/prereg_sep26.md registers.
//
// The baseline arm never reaches this page. It runs no activity between the
// initial estimate and the peer review, so it has nothing to introduce, and
// InitialPrediction keeps it out through endsPreLobbyAtInitial.
//
// Presentational only: the acknowledgement keys are written by the caller, the
// way RationaleConfirmationPage and PolishReviewPage are structured.
export function ActivityInstructions({ onContinue }) {
  const game = useGame();
  const treatment = game?.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const preLobbyMode = getPreLobbyMode(treatment);
  const usesTemplateQuestionnaire = preLobbyMode === "template";

  // Both headings have to name the screen the participant lands on next.
  // The questionnaire's own heading is hardcoded in
  // components/ReflectionActivity.jsx; the chat's comes from taskConfig, so
  // read it from there and the two cannot drift apart.
  const activityName = usesTemplateQuestionnaire
    ? "Follow-up Questionnaire"
    : taskConfig?.aiChatTitle || "AI Assistant Discussion";

  // The post-peer follow-up arms (writeup/prereg_groupfirst_sep26.md,
  // writeup/prereg_askall_sep26.md) reach this page at the same point, but
  // their next screen is the peer review and the activity comes after the
  // estimate that follows it. The page therefore describes that order, and its
  // heading names the peer-review screen (stages/Discussion.jsx).
  // The post-initial check is redundant with the caller (InitialPrediction only
  // renders this page for post-initial arms) and keeps the page unchanged for
  // the piloted post-peer arms, which never reach it.
  const isPostPeerTiming =
    isBaseRateStaticSyntheticGroupCondition(treatment) &&
    getReflectionTiming(treatment) === "post-peer" &&
    usesPostInitialConditionInstructions(treatment);
  if (isPostPeerTiming) {
    const addressesAllAnswers = usesPostPeerAskAll(treatment);
    return (
      <div className="mt-3 sm:mt-5 p-20">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Next: Group Review
        </h3>
        <div className="mt-3 mb-6 space-y-6 text-gray-800">
          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
            <p>Your estimate and your confidence rating are recorded.</p>
          </div>

          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-900 mb-3 text-lg">
              What you will do
            </h4>
            <p>
              Next, you will review{" "}
              <strong>two other participants' responses</strong> to the same
              question. You will then <strong>give your estimate again</strong>.
              You may keep it the same or change it.
            </p>
          </div>

          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-900 mb-3 text-lg">
              What comes next
            </h4>
            {usesTemplateQuestionnaire ? (
              addressesAllAnswers ? (
                <p>
                  After that, you will complete a{" "}
                  <strong>structured follow-up questionnaire</strong> about{" "}
                  <strong>each of the three answers</strong>: your own and the
                  two other participants'. Please provide{" "}
                  <strong>a detailed explanation</strong> of your reasoning.
                </p>
              ) : (
                <p>
                  After that, you will complete a{" "}
                  <strong>structured follow-up questionnaire</strong> about
                  your reasoning. Please provide{" "}
                  <strong>a detailed explanation</strong> of your reasoning.
                </p>
              )
            ) : addressesAllAnswers ? (
              <p>
                After that, you will{" "}
                <strong>discuss each of the three answers with an AI assistant</strong>,
                one at a time: your own and the two other participants'. The
                assistant asks follow-up questions. It will not tell you the
                answer and will not say whether any of the answers is correct.
                Please provide <strong>a detailed explanation</strong> of your
                reasoning.
              </p>
            ) : (
              <p>
                After that, you will{" "}
                <strong>discuss your estimate with an AI assistant</strong>. The
                assistant asks follow-up questions about your reasoning. It will
                not tell you the answer and will not say whether your answer is
                correct. Please provide <strong>a detailed explanation</strong>{" "}
                of your reasoning.
              </p>
            )}
            <p className="mt-2">
              Finally, you will submit your{" "}
              <strong>final individual estimate</strong>.
            </p>
          </div>
        </div>
        <Button handleClick={onContinue} autoFocus>
          <p>Continue</p>
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Next: {activityName}
      </h3>
      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <p>Your estimate and your confidence rating are recorded.</p>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            What you will do
          </h4>
          {usesTemplateQuestionnaire ? (
            <p>
              Before you review the other participants' responses, you will
              complete a <strong>structured follow-up questionnaire</strong>{" "}
              about your reasoning. Please provide{" "}
              <strong>a detailed explanation</strong> of your reasoning.
            </p>
          ) : (
            <p>
              Before you review the other participants' responses, you will{" "}
              <strong>discuss your estimate with an AI assistant</strong>. The
              assistant asks follow-up questions about your reasoning. It will
              not tell you the answer and will not say whether your answer is
              correct. Please provide{" "}
              <strong>a detailed explanation</strong> of your reasoning.
            </p>
          )}
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            What comes next
          </h4>
          <p>
            After the {usesTemplateQuestionnaire ? "questionnaire" : "discussion"}
            , you will confirm your <strong>pre-review prediction</strong>. You
            may keep it the same or change it. You will then review the{" "}
            <strong>two other participants' responses</strong> and submit your{" "}
            <strong>final individual estimate</strong>.
          </p>
        </div>
      </div>
      <Button handleClick={onContinue} autoFocus>
        <p>Continue</p>
      </Button>
    </div>
  );
}
